import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, Star, Users, Crown, Trophy } from "lucide-react";
import { MentorWithDetails } from "@/types/mentor";
import { MentorGamesBadges } from "@/components/mentor/MentorGamesBadges";
import { FeaturedItem } from "@/services/featuredItemsService";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 }
  }
};

interface MentorsSectionProps {
  mentors: MentorWithDetails[] | undefined;
  isLoading?: boolean;
  error?: Error | null;
  featuredItems?: FeaturedItem[];
}

export const MentorsSection: React.FC<MentorsSectionProps> = ({ mentors, isLoading, error, featuredItems }) => {
  const navigate = useNavigate();

  // Função para obter ícone da posição
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1:
        return <Crown className="h-4 w-4 text-yellow-400" />;
      case 2:
        return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3:
        return <Star className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  // Função para obter classe da borda do destaque
  const getFeaturedBorderClass = (position: number) => {
    switch (position) {
      case 1:
        return "border-yellow-400 shadow-yellow-400/20";
      case 2:
        return "border-gray-400 shadow-gray-400/20";
      case 3:
        return "border-amber-600 shadow-amber-600/20";
      default:
        return "border-[#223342]";
    }
  };

  return (
    <section id="mentors-section" className="py-16 md:py-24 px-4 relative">
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#0f141c] to-transparent z-10"></div>
      
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-12">
          <Users className="h-6 w-6 text-[#00B6F0]" />
          <h2 className="text-3xl md:text-4xl font-bold text-white">Mentores em Destaque</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="w-full max-w-sm h-96 bg-[#181D24] rounded-xl animate-pulse"></div>
            ))
          ) : error ? (
            <div className="col-span-full text-center py-12">
              <p className="text-red-400 mb-4">Erro ao carregar mentores</p>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline" 
                className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
              >
                Tentar Novamente
              </Button>
            </div>
          ) : mentors && mentors.length > 0 ? (
            mentors.map(mentor => {
              const featuredItem = featuredItems?.find(item => item.item_id === mentor.profile_id);
              const position = featuredItem?.position || 0;
              const isFeatured = !!featuredItem;
              
              return (
                <motion.div 
                  key={mentor.id} 
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  className="w-full max-w-sm relative"
                >
                  {/* Badge de posição para mentores em destaque */}
                  {isFeatured && (
                    <div className="absolute -top-2 -right-2 z-30 bg-gradient-to-r from-[#00B6F0] to-[#0099CC] rounded-full p-2 shadow-lg">
                      {getPositionIcon(position)}
                    </div>
                  )}
                  
                  <div className={`bg-gradient-to-b from-[#1E2530] to-[#0F141A] rounded-xl overflow-hidden shadow-lg border-2 ${isFeatured ? getFeaturedBorderClass(position) : 'border-[#223342]'} hover:border-[#00B6F0]/50 transition-all`}>
                    {/* Cover Image with Gradient Overlay */}
                    <div className="relative h-36">
                      <div className="absolute inset-0">
                        <img
                          src={mentor.cover_url || "/placeholder.svg"}
                          alt={`${mentor.name} cover`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder.svg";
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-[#18222F]/50 via-[#18222F]/70 to-[#1E2530] z-10"></div>
                      </div>

                      {/* Rating Badge */}
                      <div className="absolute top-3 right-3 z-20">
                        <div className="bg-[#282F3C]/80 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                          <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-white text-sm font-medium">{mentor.average_rating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Avatar - posicionado para sobrepor o banner com z-index maior */}
                    <div className="relative -mt-10 flex justify-center z-30">
                      <div className="h-20 w-20 rounded-full border-4 border-[#1E2530] overflow-hidden bg-[#1E2530] relative z-30">
                        {mentor.image_url ? (
                          <img 
                            src={mentor.image_url} 
                            alt={mentor.name}
                            className="w-full h-full object-cover"
                            onLoad={() => {
                              console.log(`✅ [MentorsSection] Image loaded for ${mentor.name} (${mentor.id})`);
                            }}
                            onError={(e) => {
                              console.log(`❌ [MentorsSection] Failed to load image for ${mentor.name} (${mentor.id}), URL:`, mentor.image_url);
                              e.currentTarget.src = "/placeholder.svg";
                            }} 
                          />
                        ) : (
                          <>
                            {console.log(`⚠️ [MentorsSection] No image URL for ${mentor.name} (${mentor.id})`)}
                            <div className="w-full h-full flex items-center justify-center bg-[#223342] text-[#00B6F0] text-xl">
                              {mentor.name.substring(0, 2).toUpperCase()}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="px-5 pt-4 pb-5">
                      {/* Mentor Name and Price */}
                      <div className="text-center mb-3">
                        <h3 className="text-xl font-bold text-white">{mentor.name}</h3>
                        {mentor.minimum_price !== null && (
                          <p className="text-sm text-gray-300 font-medium">
                            Mentoria a partir de <span className="text-[#00B6F0]">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(mentor.minimum_price / 100)}
                            </span>
                          </p>
                        )}
                      </div>

                      {/* Biografia */}
                      {mentor.bio && (
                        <div className="text-center mb-3">
                          <p 
                            className="text-sm text-gray-400 leading-relaxed"
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {mentor.bio}
                          </p>
                        </div>
                      )}
                      
                      {/* Games Tags */}
                      <div className="mb-4">
                        <MentorGamesBadges games={mentor.games} maxDisplay={2} />
                      </div>
                      
                      {/* Mentor availability badge */}
                      <div className="flex justify-center mb-4">
                        <div className="px-3 py-1 bg-[#1A2233] rounded-full flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-gray-300 text-xs">Mentor disponível</span>
                        </div>
                      </div>
                      
                      {/* View Profile Button */}
                      <Button 
                        onClick={() => navigate(`/mentores/${mentor.id}`)}
                        className="w-full bg-[#00B6F0] hover:bg-[#0099CC] text-black font-medium rounded-md"
                      >
                        Ver Perfil
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-400">Nenhum mentor encontrado</p>
            </div>
          )}
        </div>

        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate("/mentores")} 
            variant="outline" 
            size="lg" 
            className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
          >
            Ver Todos os Mentores
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};
